import type { InsightChipRef, InsightConnectivityRef, InsightGraphNode, InsightRouteEntry } from "../../types/insightApi";

/**
 * Adapter for insight-data-service's canonical topology node shape
 * (`topology.schema.ts` schemaVersion 4.0): resource nodes carry canonical
 * camelCase `type`s (`networkInterface`, `routeTable`, `securityGroup`, ...)
 * and nest their metadata under `data.attributes` / their associations under
 * `data.decorations`, instead of the previous flattened
 * `data.privateIp`/`data.effectiveNacl`/`data.securityGroups` fields the
 * TopologyV2 renderers, disclosure modules, search, and detail panel all
 * read.
 *
 * `normalizeCanonicalTopologyNodes` translates that nested canonical data
 * into the established flattened `InsightNodeData` fields once, up front, so
 * every downstream consumer keeps working unchanged. It is deliberately
 * additive and non-destructive:
 *
 * - A legacy flattened node (no `attributes`/`decorations` objects) is
 *   returned as the SAME object reference, and a node list containing no
 *   canonical nodes at all is returned as the same ARRAY reference —
 *   backward compatibility with the previous digest format is exact, not
 *   approximate.
 * - Already-flattened fields are never overwritten: a payload that carries
 *   both shapes keeps its explicit flattened values.
 * - The canonical `attributes` record is kept on `data` (the detail panel
 *   renders it), while `decorations` is dropped once fully translated so the
 *   same association isn't presented twice.
 */

/** Canonical decoration chip (`topologyDecorationChipSchema`) — `id`/`nativeId`/`label` plus fields this adapter doesn't consume. */
interface CanonicalChip {
	id?: unknown;
	nativeId?: unknown;
	label?: unknown;
}

/** Canonical route-table `attributes.connectivityTargets[]` entry (see insight-data-service `normalize-aws.ts`). */
interface CanonicalConnectivityTarget {
	targetType?: unknown;
	targetNativeId?: unknown;
	routeCount?: unknown;
	destinations?: unknown;
}

/** Earlier canonical revision's per-route `attributes.routes[]` entry (the shape the DISH staging digest still ships) — one entry per proven route, not yet aggregated per target. */
interface CanonicalRouteAttribute {
	destinationCidr?: unknown;
	gatewayNativeId?: unknown;
	gatewayType?: unknown;
}

function asString(value: unknown): string | undefined {
	return typeof value === "string" && value.length > 0 ? value : undefined;
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
	return typeof value === "object" && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : undefined;
}

/**
 * Canonical chip -> `InsightChipRef`. Canonical chips always carry
 * `id`/`nativeId`/`label` per the producer schema, but a partial payload
 * degrades through the fallback chain instead of rendering a blank chip or
 * being dropped.
 */
function toChipRef(value: unknown): InsightChipRef | null {
	const chip = asRecord(value) as CanonicalChip | undefined;
	if (!chip) return null;
	const id = asString(chip.id) ?? asString(chip.nativeId) ?? asString(chip.label);
	if (!id) return null;
	return {
		id,
		label: asString(chip.label) ?? asString(chip.nativeId) ?? id,
		nativeId: asString(chip.nativeId),
	};
}

function toChipRefs(value: unknown): InsightChipRef[] | undefined {
	if (!Array.isArray(value)) return undefined;
	return value.map(toChipRef).filter((chip): chip is InsightChipRef => chip !== null);
}

/** Canonical resource type -> the connectivity-chip `kind` the route-table card's icon/prefix map understands. */
const CONNECTIVITY_KIND_BY_TYPE: Record<string, InsightConnectivityRef["kind"]> = {
	internetGateway: "igw",
	egressOnlyInternetGateway: "egress-only-igw",
	natGateway: "nat",
	transitGateway: "tgw",
	vpnGateway: "vgw",
	virtualPrivateGateway: "vgw",
	vpcPeeringConnection: "peering",
	vpcEndpoint: "endpoint",
	directConnectGateway: "direct-connect",
	/** An ENI next hop — the appliance/NVA pattern (a vrouter or firewall instance routed to directly). */
	networkInterface: "eni",
};

/**
 * A canonical route table communicates its proven targets via
 * `attributes.connectivityTargets` (aggregated per unique target, with a
 * per-target `routeCount` and sorted `destinations`) rather than the
 * flattened `connectivity`/`routeCount`/`routes` decoration fields. Target
 * entries only carry `targetType`/`targetNativeId` — the canonical node id
 * is a server-side hash that can't be reconstructed here — so each target is
 * resolved back to its actual digest node via `idByNativeId` (built from the
 * same response) to keep chip clicks selecting the real gateway node;
 * an unresolvable target degrades to a non-navigating chip, never dropped.
 */
function translateConnectivityTargets(
	value: unknown,
	idByNativeId: Map<string, string>
): { connectivity: InsightConnectivityRef[]; routeCount: number; routes: InsightRouteEntry[] } | null {
	if (!Array.isArray(value)) return null;
	const connectivity: InsightConnectivityRef[] = [];
	const routes: InsightRouteEntry[] = [];
	let routeCount = 0;
	value.forEach((entry) => {
		const target = asRecord(entry) as CanonicalConnectivityTarget | undefined;
		if (!target) return;
		const nativeId = asString(target.targetNativeId);
		if (!nativeId) return;
		const type = asString(target.targetType) ?? "";
		const targetId = idByNativeId.get(nativeId);
		const perTargetRouteCount = typeof target.routeCount === "number" ? target.routeCount : undefined;
		connectivity.push({
			id: targetId ?? nativeId,
			nativeId,
			label: nativeId,
			kind: CONNECTIVITY_KIND_BY_TYPE[type] ?? "other",
			routeCount: perTargetRouteCount,
		});
		routeCount += perTargetRouteCount ?? 1;
		if (Array.isArray(target.destinations)) {
			target.destinations.forEach((destination) => {
				const cidr = asString(destination);
				if (cidr) routes.push({ destination: cidr, targetLabel: nativeId, targetId });
			});
		}
	});
	if (connectivity.length === 0 && routes.length === 0) return null;
	return { connectivity, routeCount, routes };
}

/**
 * Earlier canonical revision: the route table carries one `attributes.routes`
 * entry per proven route (`destinationCidr`/`gatewayType`/`gatewayNativeId`)
 * instead of the newer pre-aggregated `connectivityTargets`. Aggregated here
 * per unique target — one overview chip per route-table/target pair with its
 * own route count, never one chip per route — matching what the newer shape
 * already provides server-side.
 */
function translatePerRouteAttributes(
	value: unknown,
	idByNativeId: Map<string, string>
): { connectivity: InsightConnectivityRef[]; routeCount: number; routes: InsightRouteEntry[] } | null {
	if (!Array.isArray(value)) return null;
	const targets = new Map<string, { type: string; nativeId: string; routeCount: number }>();
	const routes: InsightRouteEntry[] = [];
	let routeCount = 0;
	value.forEach((entry) => {
		const route = asRecord(entry) as CanonicalRouteAttribute | undefined;
		if (!route) return;
		const nativeId = asString(route.gatewayNativeId);
		const destination = asString(route.destinationCidr);
		if (!nativeId && !destination) return;
		routeCount += 1;
		if (nativeId) {
			const type = asString(route.gatewayType) ?? "";
			const key = `${type}:${nativeId}`;
			const target = targets.get(key) ?? { type, nativeId, routeCount: 0 };
			target.routeCount += 1;
			targets.set(key, target);
			if (destination) routes.push({ destination, targetLabel: nativeId, targetId: idByNativeId.get(nativeId) });
		}
	});
	if (targets.size === 0 && routes.length === 0) return null;
	const connectivity = Array.from(targets.values()).map((target) => ({
		id: idByNativeId.get(target.nativeId) ?? target.nativeId,
		nativeId: target.nativeId,
		label: target.nativeId,
		kind: CONNECTIVITY_KIND_BY_TYPE[target.type] ?? "other",
		routeCount: target.routeCount,
	}));
	return { connectivity, routeCount, routes };
}

/** Whether this node uses the canonical nested shape at all — the only trigger for any translation below. */
function isCanonicalNode(node: InsightGraphNode): boolean {
	if (node.kind === "group") return false;
	const data = asRecord(node.data);
	if (!data) return false;
	return asRecord(data.attributes) !== undefined || asRecord(data.decorations) !== undefined;
}

/** Copies `value` to `target[key]` only when the payload didn't already carry an explicit flattened value. */
function setIfAbsent(target: Record<string, unknown>, key: string, value: unknown): void {
	if (value === undefined || value === null) return;
	if (target[key] !== undefined) return;
	target[key] = value;
}

/** Canonical/legacy type names that identify a route-table node — the ones that receive the implicit local route below. */
const ROUTE_TABLE_TYPES = new Set(["routeTable", "rtb", "route_table"]);

function flattenCanonicalNode(
	node: InsightGraphNode,
	idByNativeId: Map<string, string>,
	cidrById: Map<string, string>
): InsightGraphNode {
	const data = { ...(node.data as Record<string, unknown>) };
	const attributes = asRecord(data.attributes) ?? {};
	const decorations = asRecord(data.decorations) ?? {};

	setIfAbsent(data, "cidr", asString(attributes.cidrBlock));
	setIfAbsent(data, "availabilityZone", asString(attributes.availabilityZone));
	setIfAbsent(data, "privateIp", asString(attributes.privateIpAddress));
	setIfAbsent(data, "publicIp", asString(attributes.publicIp) ?? asString(attributes.publicIpAddress));

	setIfAbsent(data, "effectiveNacl", toChipRef(decorations.networkAcl) ?? toChipRefs(decorations.securityLists)?.[0]);
	setIfAbsent(data, "effectiveRouteTable", toChipRef(decorations.routeTable));
	const securityGroups = toChipRefs(decorations.securityGroups);
	if (securityGroups && securityGroups.length > 0) setIfAbsent(data, "securityGroups", securityGroups);
	setIfAbsent(data, "resolvedInstance", toChipRef(decorations.attachedInstance));

	const routeTableFields =
		translateConnectivityTargets(attributes.connectivityTargets, idByNativeId) ??
		translatePerRouteAttributes(attributes.routes, idByNativeId);
	if (routeTableFields) setIfAbsent(data, "connectivity", routeTableFields.connectivity);

	// Every AWS route table carries the non-deletable local route for its
	// VPC's CIDR — the digest deliberately omits it (only gateway-proven
	// routes are recorded), which made local-only tables read as "no routes
	// at all". Synthesized here from the parent VPC's CIDR so the count and
	// the route list are never dishonestly empty.
	if (ROUTE_TABLE_TYPES.has(node.type ?? "")) {
		const vpcCidr = node.parentId ? cidrById.get(node.parentId) : undefined;
		const gatewayRoutes = routeTableFields?.routes ?? [];
		const gatewayRouteCount = routeTableFields?.routeCount ?? 0;
		if (vpcCidr) {
			setIfAbsent(data, "routeCount", gatewayRouteCount + 1);
			setIfAbsent(data, "routes", [{ destination: vpcCidr, targetLabel: "local" }, ...gatewayRoutes]);
		} else if (routeTableFields) {
			setIfAbsent(data, "routeCount", gatewayRouteCount);
			if (gatewayRoutes.length > 0) setIfAbsent(data, "routes", gatewayRoutes);
		}
	} else if (routeTableFields) {
		setIfAbsent(data, "routeCount", routeTableFields.routeCount);
		if (routeTableFields.routes.length > 0) setIfAbsent(data, "routes", routeTableFields.routes);
	}

	// Fully translated above — keeping it would present every association twice
	// (once as a rendered chip, once as a raw-JSON detail row).
	delete data.decorations;

	return { ...node, data };
}

/**
 * Translates every canonical nested node in `nodes` into the flattened
 * `InsightNodeData` shape; legacy flattened nodes (and group nodes) pass
 * through as the same object reference. Returns the input array reference
 * unchanged when nothing needed translation, so the previous digest format's
 * render path is byte-for-byte identical.
 */
export function normalizeCanonicalTopologyNodes(nodes: InsightGraphNode[]): InsightGraphNode[] {
	if (!nodes.some(isCanonicalNode)) return nodes;

	// nativeId -> node id, for resolving a route table's connectivity targets
	// (which only carry the target's cloud-native id) back to real digest
	// nodes. First occurrence wins — nativeIds are unique per digest scope.
	const idByNativeId = new Map<string, string>();
	// node id -> its own CIDR, for synthesizing a route table's implicit
	// local route from its parent VPC.
	const cidrById = new Map<string, string>();
	nodes.forEach((node) => {
		const data = asRecord(node.data);
		const nativeId = asString(data?.nativeId);
		if (nativeId && !idByNativeId.has(nativeId)) idByNativeId.set(nativeId, node.id);
		const cidr = asString(data?.cidr) ?? asString(asRecord(data?.attributes)?.cidrBlock);
		if (cidr) cidrById.set(node.id, cidr);
	});

	return nodes.map((node) => (isCanonicalNode(node) ? flattenCanonicalNode(node, idByNativeId, cidrById) : node));
}
